/**
 * Frontend Sentry initialization with PIPA-aware PII scrubbing (issue #63).
 *
 * Companion to V3/backend/sentry_init.py — same conservative posture:
 * Sentry's default browser capture pulls a lot of data we cannot ship
 * to a foreign error tracker without violating PIPA's cross-border
 * disclosure obligations. localStorage carries student tokens
 * (studentToken, devTeacherToken) and the lastPassword cache;
 * URL paths leak session IDs (/student/session/{id}); breadcrumbs
 * occasionally include API request bodies that carry student input.
 *
 * This module centralizes:
 *
 *   1. Whether to init Sentry at all (gated on VITE_SENTRY_DSN being
 *      set at build time — unset = no-op, useful for local dev).
 *   2. The `beforeSend` scrubber that strips PII before transmit.
 *   3. The `environment` tag (staging vs production) sourced from
 *      VITE_SENTRY_ENVIRONMENT or, if unset, defaulting per build mode.
 *   4. The release tag (git SHA) so errors get attributed to the
 *      deploy they originated from. Cloudflare Pages exposes
 *      CF_PAGES_COMMIT_SHA at build time; we surface it as
 *      __BUILD_BRANCH__ already (vite.config.ts) and pull a similar
 *      pattern here for the SHA.
 *
 * When VITE_SENTRY_DSN is absent, initSentry() returns without
 * importing @sentry/react — the dependency is bundled but the SDK is
 * never instantiated. Local dev, the test suite, and any operational
 * pause of Sentry all work with the env var unset.
 */

// URL prefixes whose breadcrumb capture should be redacted because
// the request body or URL itself contains student-supplied data.
const BREADCRUMB_REDACT_PATH_PREFIXES = [
  '/api/sessions',
  '/api/dev/quickstart',
  '/api/admin',
] as const

// Local-storage keys whose values we never want crossing the border.
// The SDK's default browser integration occasionally captures
// localStorage state in error context; this ensures those keys are
// always-redacted defensively.
const LOCALSTORAGE_REDACT_KEYS = [
  'studentToken',
  'devTeacherToken',
  'devTeacherData',
  'devQuickstartLastPassword',
  'siteAccess',
  'teacherClassCode',
  'studentLoginData',
] as const

// Headers we strip from any captured fetch breadcrumb.
const HEADER_REDACT = new Set([
  'authorization',
  'cookie',
  'x-site-password',
  'x-clerk-auth-token',
])

interface Breadcrumb {
  category?: string
  message?: string
  data?: unknown
}

interface SentryEvent {
  request?: {
    url?: string
    headers?: Record<string, string>
    data?: unknown
    query_string?: string
  }
  breadcrumbs?: Breadcrumb[]
  user?: { id?: string; email?: string; ip_address?: string }
  contexts?: Record<string, unknown>
}

function shouldRedactPath(url: string | undefined): boolean {
  if (!url) return false
  return BREADCRUMB_REDACT_PATH_PREFIXES.some((prefix) =>
    url.includes(prefix),
  )
}

function scrubHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!headers) return headers
  const cleaned: Record<string, string> = {}
  for (const [k, v] of Object.entries(headers)) {
    cleaned[k] = HEADER_REDACT.has(k.toLowerCase()) ? '[redacted]' : v
  }
  return cleaned
}

function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  // fetch-style breadcrumbs Sentry generates from intercepted XHR /
  // fetch calls land here. The crumb.data carries `url`, `method`,
  // `status_code`, sometimes `request_body_size`. We strip the body
  // and redact the URL on PII-bearing paths so the structural
  // crumb (route + method + status) is preserved without payload.
  if (crumb.data && typeof crumb.data === 'object') {
    const data = crumb.data as { url?: string; body?: unknown }
    if (shouldRedactPath(data.url)) {
      crumb.data = { ...data, body: '[redacted]' }
    }
    // Session slugs never cross the border, even inside crumb URLs: the
    // SDK's default fetch-breadcrumb integration records every API call's
    // full URL, so without this a `/api/sessions/sess_.../message` crumb
    // would attach the raw slug to ANY later event — including the #1107
    // `client_request_timeout` beacon, whose tag-level slug scrubbing in
    // `api/client.ts` would otherwise be defeated by its own breadcrumb
    // trail. Replace the slug segment, keep the structural route.
    if (typeof data.url === 'string' && data.url.includes('/sess_')) {
      crumb.data = {
        ...(crumb.data as Record<string, unknown>),
        url: data.url.replace(/\/sess_[A-Za-z0-9_-]+/g, '/{slug}'),
      }
    }
  }
  // Long messages get truncated — we occasionally log debug info that
  // contains transient tokens; truncating to 100 chars keeps the
  // structural shape (which file logged this, what level, what
  // category) while dropping the tail that's most likely to leak.
  if (typeof crumb.message === 'string' && crumb.message.length > 100) {
    crumb.message = crumb.message.slice(0, 100) + '…[truncated]'
  }
  return crumb
}

function scrubEvent(event: SentryEvent): SentryEvent | null {
  // Request block: strip auth headers + redact body/query on PII paths.
  if (event.request) {
    event.request.headers = scrubHeaders(event.request.headers)
    if (shouldRedactPath(event.request.url) && 'data' in event.request) {
      event.request.data = '[redacted]'
    }
    delete event.request.query_string
  }

  // Breadcrumbs: scrub each.
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb)
  }

  // User block: replace with coarse "is anything set" boolean.
  if (event.user) {
    event.user = event.user.id ? { id: '[redacted]' } : {}
  }

  // Contexts can carry localStorage snapshots in Sentry's "browser"
  // context. Walk and redact our sensitive keys defensively.
  if (event.contexts && typeof event.contexts === 'object') {
    for (const ctx of Object.values(event.contexts)) {
      if (ctx && typeof ctx === 'object') {
        const c = ctx as Record<string, unknown>
        for (const key of LOCALSTORAGE_REDACT_KEYS) {
          if (key in c) c[key] = '[redacted]'
        }
      }
    }
  }

  return event
}

export async function initSentry(): Promise<boolean> {
  const dsn = (import.meta.env.VITE_SENTRY_DSN ?? '').trim()
  if (!dsn) return false

  // Lazy-import so the SDK only loads when actually configured. Saves
  // bundle size in dev runs that don't have a DSN set, and keeps the
  // unit tests fast (vitest doesn't pull Sentry into jsdom every
  // file).
  const Sentry = await import('@sentry/react')

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    (import.meta.env.PROD ? 'production' : 'development')

  // Same release-tagging strategy as the backend: if the deploy set
  // VITE_SENTRY_RELEASE explicitly, use that; otherwise fall back to
  // the build branch (vite.config.ts injects __BUILD_BRANCH__) so at
  // least which branch built the bundle is visible.
  const release =
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ||
    (typeof __BUILD_BRANCH__ === 'string' ? __BUILD_BRANCH__ : undefined)

  const tracesSampleRate = parseFloat(
    (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string) || '0.1',
  )

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      // Sentry's BrowserTracing used to be implicit — explicit now.
      // Catches navigation + fetch performance. Sample-rate gated.
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: Number.isFinite(tracesSampleRate)
      ? tracesSampleRate
      : 0.1,
    sendDefaultPii: false,
    beforeSend: (event) => scrubEvent(event as SentryEvent) as never,
  })

  // Expose the loaded SDK on ``window.Sentry`` so deferred, non-React
  // capture sites can reach it WITHOUT statically importing
  // ``@sentry/react`` (which would pull the SDK into their eager bundle
  // and regress the boot-perf this lazy-init pattern protects). The npm
  // ``@sentry/react`` package — unlike the CDN loader bundle — does not
  // self-attach this global, so we set it explicitly. Two consumers rely
  // on it: ``RouteErrorBoundary`` (``'Sentry' in window``) and the
  // client-side timeout beacon in ``src/api/client.ts`` (#1107). Both
  // guard on the property existing and swallow capture failures, so the
  // global is a best-effort convenience, never a hard dependency.
  try {
    ;(window as unknown as { Sentry: unknown }).Sentry = Sentry
  } catch {
    // ``window`` unavailable (SSR / hardened sandbox) — capture sites
    // fall back to console-only, same as when Sentry isn't configured.
  }

  return true
}
